import { Notification } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { type NotificationData, Notifications, notifications } from '@mantine/notifications'
import { IconAlertCircleFilled, IconAlertTriangleFilled, IconCircleCheckFilled, IconInfoCircleFilled } from '@tabler/icons-react'
import type { Tone } from '@tnn/core'
import type { CSSProperties } from 'react'
import classes from './Toast.module.css'

/** The Mantine colours call sites pass to `notifications.show`, read as a status tone */
const toneOf: Record<string, Tone> = {
  red: 'danger',
  danger: 'danger',
  orange: 'warning',
  yellow: 'warning',
  warning: 'warning',
  blue: 'info',
  info: 'info',
  green: 'success',
  success: 'success',
}

const iconOf: Partial<Record<Tone, typeof IconCircleCheckFilled>> = {
  danger: IconAlertCircleFilled,
  warning: IconAlertTriangleFilled,
  info: IconInfoCircleFilled,
}

/**
 * Every toast in every app (`notifications.show` from `@mantine/notifications`): a snackbar on
 * the inverse surface with a status icon picked from its colour, so call sites only pass a
 * message and, for problems, `color: 'danger'`. Bottom-right on desktop; full width above the
 * bottom navigation on phones.
 */
export function Toasts() {
  const phone = useMediaQuery('(max-width: 47.99em)')
  return (
    <Notifications
      position={phone ? 'bottom-center' : 'bottom-right'}
      limit={3}
      autoClose={4000}
      containerWidth={420}
      classNames={{ root: classes.host, notification: classes.item }}
      renderNotification={(data) => <Toast {...data} />}
    />
  )
}

function Toast({ id, message, title, color, icon, loading, withCloseButton, allowClose }: NotificationData) {
  const tone = color ? toneOf[color] : undefined
  const Icon = (tone && iconOf[tone]) ?? IconCircleCheckFilled
  return (
    <Notification
      // The wrapper Mantine renders around a custom toast already carries role="alert"
      role="presentation"
      classNames={classes}
      style={{ '--toast-tone': tone ? `var(--tnn-tone-${tone}-inverse)` : 'var(--tnn-inverse-primary)' } as CSSProperties}
      title={title}
      icon={icon ?? <Icon size={20} />}
      loading={loading}
      loaderProps={{ size: 20, color: 'var(--toast-tone)' }}
      withCloseButton={withCloseButton !== false && allowClose !== false}
      onClose={() => id && notifications.hide(id)}
    >
      {message}
    </Notification>
  )
}
